import React, { useState, useRef, useEffect } from 'react';
import { useT, Language } from './i18n/index';

// Inline SVG flags instead of Unicode flag emoji (🇬🇧/🇳🇱): those are two-codepoint
// "regional indicator" sequences that several common emoji fonts (notably on Windows
// and many Linux setups) don't compose into a flag glyph — they fall back to showing
// the raw letter pair ("GB"/"NL") instead. SVG renders identically everywhere.
function FlagGB({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 60 36" style={{ display: 'block', borderRadius: 2, flexShrink: 0 }}>
      <rect width="60" height="36" fill="#012169" />
      <path d="M0,0 L60,36 M60,0 L0,36" stroke="#fff" strokeWidth="7.2" />
      <path d="M0,0 L60,36 M60,0 L0,36" stroke="#C8102E" strokeWidth="4.8" />
      <path d="M30,0 V36 M0,18 H60" stroke="#fff" strokeWidth="12" />
      <path d="M30,0 V36 M0,18 H60" stroke="#C8102E" strokeWidth="7.2" />
    </svg>
  );
}
function FlagNL({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 60 36" style={{ display: 'block', borderRadius: 2, flexShrink: 0 }}>
      <rect width="60" height="12" y="0" fill="#AE1C28" />
      <rect width="60" height="12" y="12" fill="#fff" />
      <rect width="60" height="12" y="24" fill="#21468B" />
    </svg>
  );
}

const OPTIONS: { code: Language; Flag: (props: { size?: number }) => JSX.Element; label: string; short: string }[] = [
  { code: 'en', Flag: FlagGB, label: 'English', short: 'EN' },
  { code: 'nl', Flag: FlagNL, label: 'Nederlands', short: 'NL' },
];

export default function LanguageSwitcher() {
  const { language, setLanguage } = useT();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen(v => !v);
  };

  const current = OPTIONS.find(o => o.code === language) ?? OPTIONS[0];

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        title="Language / Taal"
        style={{
          height: 28,
          minHeight: 28,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: open ? '#eff6ff' : 'white',
          border: `1px solid ${open ? '#93c5fd' : '#d1d5db'}`,
          borderRadius: 6,
          cursor: 'pointer',
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 600,
          color: open ? '#2563eb' : '#374151',
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <current.Flag size={16} />
        <span style={{ fontSize: 11 }}>{current.short}</span>
      </button>

      {open && pos && (
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: pos.top,
            right: pos.right,
            zIndex: 2000,
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            border: '1px solid #e5e7eb',
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minWidth: 180,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px 6px' }}>
            Language / Taal
          </div>
          {OPTIONS.map(opt => (
            <button
              key={opt.code}
              onClick={() => { setLanguage(opt.code); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                border: opt.code === language ? '1.5px solid #93c5fd' : '1.5px solid transparent',
                background: opt.code === language ? '#eff6ff' : 'transparent',
                color: opt.code === language ? '#1d4ed8' : '#374151',
                fontWeight: opt.code === language ? 700 : 500,
                fontSize: 14,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { if (opt.code !== language) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (opt.code !== language) e.currentTarget.style.background = 'transparent'; }}
            >
              <opt.Flag size={22} />
              <span>{opt.label}</span>
              {opt.code === language && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#3b82f6' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
